import React, { useState, useEffect } from 'react';
import {array, bool, func, string} from 'prop-types';
import {find} from 'lodash';

import { makeStyles } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';

import Modal from '../../../Modal';
import AutocompleteCreate from '../../../AutocompleteCreate';
import FilePicker from '../../../FilePicker/FilePicker';
import { useFetchResource } from '../../../../helpers/hooks';
import { onlineHubHost } from '../../../../config';

import './FirmwareForm.css';

const useSelectStyles = makeStyles((theme) => ({
  formControl: {
    width: '100%',
    marginBottom: '15px'
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
}));

const useInputStyles = makeStyles(() => ({
  root: {
    marginBottom: '15px'
  }
}));

const useSubmitButtonStyles = makeStyles(() => ({
  root: {
    marginLeft: '20px'
  }
}));

const useLoadingStyles = makeStyles(() => ({
  root: {
    position: 'absolute',
    right: '30px'
  }
}));

function getErrorInputs(inputsByKey) {
  return Object.keys(inputsByKey).reduce((soFar, key) => !inputsByKey[key] ? soFar.concat([key]) : soFar, []);
}

export default function FirmwareForm({itemId, items, active, onClose, onSubmit}) {
  const selectClasses = useSelectStyles();
  const inputClasses = useInputStyles();
  const submitButtonClasses = useSubmitButtonStyles();
  const loadingClasses = useLoadingStyles();

  const [product, setProduct] = useState('brightSign');
  const [target, setTarget] = useState('');
  const [version, setVersion] = useState('');
  const [firmwareFile, setFirmwareFile] = useState(null);
  const [errorInputs, setErrorInputs] = useState([]);
  const [loading, setLoading] = useState(false);

  const {data, retry} = useFetchResource(`${onlineHubHost}/app/firmwares/${product}/targets`);
  const targets = data || [];

  useEffect(() => {
    if (active) {
      retry();
    } else {
      setProduct('brightSign');
      setTarget('');
      setVersion('');
      setErrorInputs([]);
      setLoading(false);
      setFirmwareFile(null);
    }
  }, [active, retry])

  useEffect(() => {
    if (active && itemId) {
      const item = find(items, {id: itemId});
      setProduct(item.product);
      setTarget(item.target);
      setVersion(item.version);
    }
  }, [active, itemId, items])

  function handleInputChange({target: {value, id}}) {
    if (id === 'firmwareForm-input-version') {
      setVersion(value);
    }
  }

  function handleSelectChange({target: {value, id}}) {
    if (id === 'firmwareForm-select-product') {
      setProduct(value);
    }
  }

  function handleTargetChange(value) {
    setTarget(value);
  }

  function handleFirmwareFileSelect(file) {
    setFirmwareFile(file);
  }

  function handleSubmit() {
    const data = itemId ? {target, version} : {product, target, version, firmwareFile};
    const errorInputs = getErrorInputs(data);
    setErrorInputs(errorInputs);
    if (!errorInputs.length) {
      setLoading(true);
      onSubmit(data);
    }
  }

  return (
    <Modal active={active} onClose={onClose}>
      <div className="FirmwareForm">
        <h2 className="FirmwareForm-header">{'Add firmware'}</h2>
        {!itemId
          ? <FormControl className={selectClasses.formControl}>
              <InputLabel id="firmwareForm-select-product-label">Product</InputLabel>
              <Select
                labelId="firmwareForm-select-product-label"
                id="firmwareForm-select-product"
                value={product}
                onChange={handleSelectChange}
              >
                <MenuItem value="default">Default</MenuItem>
                <MenuItem value="brightSign">BrightSign</MenuItem>
              </Select>
            </FormControl>
        : null}
        <AutocompleteCreate
          classes={{
            root: inputClasses.root
          }}
          value={target}
          error={errorInputs.includes('target')}
          label="Target"
          source={targets}
          onChange={handleTargetChange}
        />
        <div className="FirmwareForm-autorun">
          <TextField
            id="firmwareForm-input-version"
            classes={{
              root: inputClasses.root
            }}
            value={version}
            label="Version"
            fullWidth
            required
            error={errorInputs.includes('version')}
            onChange={handleInputChange}
          />
          <FilePicker
            className="FirmwareForm-autorun-filePicker"
            accept=".bsfw,.fw"
            onSelect={handleFirmwareFileSelect}
          />
        </div>
        <div className="FirmwareForm-footer">
          <Button variant="contained" disabled={loading} onClick={onClose}>cancel</Button>
          <Button
            classes={{root: submitButtonClasses.root}}
            variant="contained"
            color="primary"
            disabled={loading}
            onClick={handleSubmit}
          >
            submit
          </Button>
          {loading && <CircularProgress size={24} className={loadingClasses.root} />}
        </div>
      </div>
    </Modal>
  );
}

FirmwareForm.propTypes = {
  itemId: string,
  items: array,
  active: bool,
  duplicate: bool,
  onSubmit: func,
  onClose: func
}
